import React, { useState } from 'react';
import axios from 'axios';
import { Cloud, AlertTriangle, CheckCircle, Wind, Droplets, ThermometerSun, Calendar, MapPin, Loader2, TrendingUp, Shield } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WeatherSafetyForecast = () => {
  const [formData, setFormData] = useState({
    location: '',
    workplace_type: 'construction',
    days: 7
  });
  const [loading, setLoading] = useState(false);
  const [forecast, setForecast] = useState(null);
  const [error, setError] = useState(null);

  const workplaceTypes = [
    { value: 'construction', label: 'Construction Site' },
    { value: 'outdoor_maintenance', label: 'Outdoor Maintenance' },
    { value: 'roofing', label: 'Roofing Operations' },
    { value: 'electrical_work', label: 'Electrical Work' },
    { value: 'equipment_operation', label: 'Equipment Operation' },
    { value: 'material_handling', label: 'Material Handling' },
    { value: 'landscaping', label: 'Landscaping' },
    { value: 'warehouse_outdoor', label: 'Outdoor Warehouse' }
  ];

  const getRiskColor = (risk) => {
    const colors = {
      low: 'bg-green-100 text-green-700 border-green-300',
      medium: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      high: 'bg-orange-100 text-orange-700 border-orange-300',
      critical: 'bg-red-100 text-red-700 border-red-300'
    };
    return colors[risk] || 'bg-gray-100 text-gray-700';
  };

  const getRiskIcon = (risk) => {
    if (risk === 'low') return <CheckCircle className="w-5 h-5" />;
    if (risk === 'medium') return <AlertTriangle className="w-5 h-5" />;
    if (risk === 'high' || risk === 'critical') return <AlertTriangle className="w-5 h-5" />;
    return <Cloud className="w-5 h-5" />;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/weather-safety/forecast`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setForecast(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate weather-safety forecast');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cloud className="w-6 h-6" />
            Weather-Safety Forecast
          </CardTitle>
          <CardDescription>
            Get AI-powered safety analysis combining weather forecasts with workplace hazard prevention
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  placeholder="City name or lat,lon"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  Example: &quot;New York&quot; or &quot;40.7128,-74.0060&quot;
                </p>
              </div>

              <div>
                <Label htmlFor="workplace_type">Workplace Type</Label>
                <select
                  id="workplace_type"
                  value={formData.workplace_type}
                  onChange={(e) => setFormData({ ...formData, workplace_type: e.target.value })}
                  className="w-full border rounded-md p-2"
                >
                  {workplaceTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="days">Forecast Period</Label>
                <select
                  id="days"
                  value={formData.days}
                  onChange={(e) => setFormData({ ...formData, days: parseInt(e.target.value) })}
                  className="w-full border rounded-md p-2"
                >
                  <option value={7}>7-Day Forecast</option>
                  <option value={10}>10-Day Forecast</option>
                </select>
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full">
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing Weather & Safety...</>
              ) : (
                <><Cloud className="w-4 h-4 mr-2" /> Generate Safety Forecast</>
              )}
            </Button>
          </form>

          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {forecast && (
        <div className="space-y-6">
          {/* Overall Risk Assessment */}
          <Card className={`border-2 ${getRiskColor(forecast.overall_risk_assessment.level)}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-6 h-6" />
                  Overall Risk Assessment
                </CardTitle>
                <Badge className={getRiskColor(forecast.overall_risk_assessment.level)}>
                  {forecast.overall_risk_assessment.level.toUpperCase()}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-slate-600" />
                  <div>
                    <p className="font-semibold">{forecast.location}</p>
                    <p className="text-sm text-slate-600">
                      {forecast.workplace_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                  </div>
                </div>

                <Alert className="bg-white">
                  <AlertDescription className="text-slate-700">
                    {forecast.overall_risk_assessment.message}
                  </AlertDescription>
                </Alert>

                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold">Mitigation Score:</span>
                  <span className="text-2xl font-bold text-blue-600">
                    {forecast.overall_risk_assessment.avg_mitigation_score}
                  </span>
                  <span className="text-slate-600">/100</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Daily Forecasts */}
          <div className="grid md:grid-cols-2 gap-4">
            {forecast.forecasts.map((day, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      {new Date(day.date).toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </CardTitle>
                    <Badge className={getRiskColor(day.risk_level)}>
                      {getRiskIcon(day.risk_level)}
                      {day.risk_level}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Weather Conditions */}
                  <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <ThermometerSun className="w-4 h-4 text-orange-500" />
                      <span className="text-sm font-semibold">{Math.round(day.temperature)}°F</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Droplets className="w-4 h-4 text-blue-500" />
                      <span className="text-sm">{Math.round(day.precipitation_probability)}% rain</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Wind className="w-4 h-4 text-slate-500" />
                      <span className="text-sm">{Math.round(day.wind_speed)} mph</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Cloud className="w-4 h-4 text-slate-500" />
                      <span className="text-sm capitalize">{day.conditions}</span>
                    </div>
                  </div>

                  {/* Identified Hazards */}
                  <div>
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4 text-orange-500" />
                      Identified Hazards:
                    </h4>
                    <ul className="space-y-1">
                      {day.identified_hazards.map((hazard, i) => (
                        <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                          <span className="text-orange-500 mt-1">•</span>
                          <span>{hazard}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Safety Recommendations */}
                  <div>
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-1">
                      <Shield className="w-4 h-4 text-green-600" />
                      Safety Recommendations:
                    </h4>
                    <ul className="space-y-1">
                      {day.safety_recommendations.map((rec, i) => (
                        <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Mitigation Score */}
                  <div className="pt-3 border-t">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold">Mitigation Score:</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              day.accident_mitigation_score >= 80 ? 'bg-green-500' :
                              day.accident_mitigation_score >= 60 ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${day.accident_mitigation_score}%` }}
                          />
                        </div>
                        <span className="text-sm font-bold">{Math.round(day.accident_mitigation_score)}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WeatherSafetyForecast;
